.timeline {
  display: grid;
  /* The lane takes whatever the elastic timeline column got (ui-redesign §4.1): the column
     is minmax(388px, 1fr) in DayView, so the lane never falls below 328px. */
  grid-template-columns: var(--col-axis) minmax(0, 1fr);
  column-gap: 8px;
  height: 100%;
  /* 24 hours at the minimum 22 px each, plus room for the first and last label. */
  min-height: calc(24 * 22px + 20px);
  padding: 10px 0;
}

/* ---- axis ---- */
.axis {
  position: relative;
}

.axisLabel {
  position: absolute;
  left: 0;
  transform: translateY(-50%);
  font-size: var(--font-size-sm);
  line-height: 1.1;
  color: var(--text-muted);
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.axisNextDay {
  color: var(--text);
  font-weight: 600;
}

.nextDay {
  display: block;
  margin-top: 1px;
  font-size: var(--font-size-xs);
  font-weight: 500;
  color: var(--accent);
}

.axisNow {
  position: absolute;
  right: 0;
  transform: translateY(-50%);
  z-index: 2;
  padding: 1px 5px;
  border-radius: 8px;
  background: var(--now);
  box-shadow: 0 0 0 1px var(--white);
  color: var(--white);
  font-size: var(--font-size-xs);
  line-height: 1.3;
  font-variant-numeric: tabular-nums;
}

/* Pills marking the start and end of a drag on the axis (ui-redesign §3.4.5). */
.axisPill {
  position: absolute;
  right: 0;
  transform: translateY(-50%);
  z-index: 3;
  padding: 1px 5px;
  border-radius: 8px;
  background: var(--accent);
  color: var(--white);
  font-size: var(--font-size-xs);
  line-height: 1.3;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  pointer-events: none;
}

/* Faint pill at the start of the hover ghost. */
.axisPillSoft {
  background: var(--accent-soft);
  color: var(--accent);
}

/* ---- lane ---- */
.lane {
  position: relative;
  height: 100%;
  border-bottom: 1px solid var(--line-hour);
  /* L0 of the day view, drawn once on the lane itself (ui-redesign §3.1.1; iteration 2026-10c
     §1): exactly two layers, independent of the time step. A dark 1px line every 2 hours
     (1/12 of the lane, aligned with the axis labels) and a light 1px line every hour (1/24).
     Sizes stay percentages so the lines track the percent-positioned axis labels exactly. */
  background-image:
    linear-gradient(to bottom, var(--line-hour) 1px, transparent 1px),
    linear-gradient(to bottom, var(--line-unit) 1px, transparent 1px);
  background-size:
    100% calc(100% / 12),
    100% calc(100% / 24);
  cursor: cell;
  touch-action: pan-y;
  user-select: none;
  -webkit-user-select: none;
  -webkit-touch-callout: none;
  /* The lane is the query container of its blocks: cqi below is 1% of the lane's content
     width. The name lets block children query the lane (not the block, which is a container
     of its own) for the note line's 440px threshold (ui-redesign §3.7). */
  container-type: inline-size;
  container-name: lane;
  /* The create band: blocks stop short of the lane's right edge, leaving a one-click overlay
     entry (ui-redesign §3.1.3). 328px lane ≈ 20px; from a 667px lane upwards 40px. */
  --create-band: clamp(16px, 6cqi, 40px);
}

/* The week view draws L0 once for all seven lanes (WeekGridLayer); the lanes stay transparent. */
.weekLane {
  border-bottom: 0;
  background-image: none;
  --create-band: 12px;
}

@media (hover: none) {
  .lane {
    --create-band: 24px;
  }

  .weekLane {
    --create-band: 12px;
  }
}

/* The 6px column gap right of a week lane is merged into the create band's hit area: pointer
   events over the gap land here and bubble to the lane as an "empty" gesture. */
.bandHit {
  position: absolute;
  top: 0;
  bottom: 0;
  right: -6px;
  width: 18px;
  touch-action: pan-y;
}

.quickMark {
  cursor: crosshair;
}

.sleep {
  position: absolute;
  left: 0;
  right: 0;
  z-index: 1; /* above the transparent gap slots, below the blocks */
  background: var(--sleep-band);
  /* The band is an opaque token (§5.1): multiply keeps the grid lines visible through it. */
  mix-blend-mode: multiply;
  pointer-events: none;
}

/* A free stretch is not a card: the grid stays visible through it. The slot remains a focusable
   "+ Log" item for the keyboard and screen readers (ui-redesign §3.1.2). */
.gap {
  position: absolute;
  left: 0;
  width: 100%;
  display: flex;
  align-items: center;
  padding: 0 10px;
  border: 0;
  border-radius: var(--radius-sm);
  background: transparent;
  color: var(--text-muted);
  font-size: var(--font-size-sm);
  text-align: left;
  cursor: cell;
  touch-action: pan-y;
}

.quickMark .gap {
  cursor: crosshair;
}

.gap:focus-visible {
  outline: 2px dashed var(--accent);
  outline-offset: -2px;
}

.gapLabel {
  pointer-events: none;
  /* Mouse: only the hover ghost marks a free stretch; the caption appears on keyboard focus. */
  visibility: hidden;
}

/* Touch devices show a quiet "+ Log" hint in free stretches of the day view. */
@media (hover: none) {
  .gapLabel {
    visibility: visible;
  }

  .weekLane .gapLabel {
    visibility: hidden;
  }
}

.gap:focus-visible .gapLabel {
  visibility: visible;
  color: var(--accent);
}

/* While a span is being dragged out, the "+ Log" captions would show through its preview. */
[data-dragging] .gapLabel {
  visibility: hidden;
}

/* ---- blocks ---- */
.block {
  position: absolute;
  z-index: 2;
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-width: 0;
  padding: 0 10px;
  /* Also clips the 44×44px touch handles to their own block, so the body of an adjacent block
     always wins the touch hit test (§3.3); e2e/tests/interaction-wp4.spec.ts guards it. */
  overflow: hidden;
  border: 1px solid color-mix(in srgb, var(--block-color) 45%, var(--white));
  border-left: 4px solid var(--block-color);
  border-radius: var(--radius-block);
  /* Saturated Outlook-style tints (§5.4): 30% normal, 36% hover, 42% selected or dragging. */
  background: color-mix(in srgb, var(--block-color) 30%, var(--white));
  /* Half project colour, half black: at least 4.5:1 on all three tints for all ten colours. */
  color: color-mix(in srgb, var(--block-color) 50%, var(--black));
  cursor: pointer;
  touch-action: pan-y;
  transition: background-color 100ms ease-out;
  /* Lets the conflict badge adapt to the width of its own block (see @container below). */
  container-type: inline-size;
}

.block:hover:not(.selected, .draft, .tiny, .narrow) {
  background: color-mix(in srgb, var(--block-color) 36%, var(--white));
}

.movable {
  cursor: grab;
}

.dragging {
  cursor: grabbing;
  box-shadow: var(--shadow-lift);
  z-index: 3;
}

.dragging:not(.draft, .tiny, .narrow) {
  background: color-mix(in srgb, var(--block-color) 42%, var(--white));
}

.draft {
  border: 1.5px dashed color-mix(in srgb, var(--block-color) 70%, var(--white));
  border-left: 4px solid color-mix(in srgb, var(--block-color) 55%, var(--white));
  /* Lighter but opaque: the hour lines of the lane must not show through. */
  background: color-mix(in srgb, var(--block-color) 8%, var(--white));
  /* Same text colour as a confirmed block: a draft is told apart by its dashed border, tint and badge. */
  color: color-mix(in srgb, var(--block-color) 50%, var(--black));
}

/* The block "now" sits in: a soft outer ring and the "now" badge (§5.4). */
.current {
  box-shadow: 0 0 0 2px var(--accent-soft-strong);
}

/* Selected or keyboard-focused: 42% tint under a white inner ring and an accent outer ring (§5.4). */
.selected {
  box-shadow:
    0 0 0 2px var(--white),
    0 0 0 4px var(--accent);
}

.selected:not(.draft, .tiny, .narrow) {
  background: color-mix(in srgb, var(--block-color) 42%, var(--white));
}

/* An overlap is ordinary recording, not an error: no red. The side-by-side columns re-arrange
   150ms after a drop (ui-redesign §3.1.3: the layout is frozen while dragging, since the data
   only changes on release; the delayed instant transition is the re-arrangement). */
.conflicting {
  transition:
    left 0s 150ms,
    width 0s 150ms;
}

/* The overlap popover is open for this block or one it overlaps: 2px accent ring (§3.8). */
.resolving {
  z-index: 4;
  outline: 2px solid var(--accent);
  outline-offset: 1px;
}

/* An overlapping block is narrow: only its name is shown, never fewer than three characters. */
.conflicting .meta {
  display: none;
}

.conflicting .name {
  min-width: min(3em, 100%);
}

/* A column too narrow for text (ui-redesign §3.1.4): colour only, like a tiny block. */
.narrow {
  padding: 0;
  border: 0;
  border-left: 4px solid var(--block-color);
  background: var(--block-color);
}

/* The stretch whose minutes count towards another block: 135° hatch (ui-redesign §5.4).
   Thin and light, and painted under the block's text (z-index -1 inside the block's own
   stacking context), so the name and time stay legible. */
.hatch {
  position: absolute;
  z-index: -1;
  right: 0;
  left: 0;
  pointer-events: none;
  background-image: repeating-linear-gradient(
    135deg,
    color-mix(in srgb, var(--block-color) 16%, transparent) 0 1px,
    transparent 1px 7px
  );
}

.compact {
  padding: 0 8px;
  font-size: var(--font-size-sm);
}

.tiny {
  padding: 0;
  border: 0;
  border-left: 4px solid var(--block-color);
  /* No text fits: solid project colour is the only information (§5.4 过短). */
  background: var(--block-color);
}

.tiny.draft,
.narrow.draft {
  border: 1.5px dashed color-mix(in srgb, var(--block-color) 70%, var(--white));
  background: color-mix(in srgb, var(--block-color) 28%, var(--white));
}


.blockBody {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  min-height: 0;
  /* Text row cap (ui-redesign §3.7): in a 1095px lane the time must not sit at the far edge. */
  max-width: 480px;
}

.name {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: 500;
}

.badge {
  flex: none;
  padding: 0 6px;
  background: var(--surface);
  border: 1px solid currentcolor;
  border-radius: 10px;
  font-size: var(--font-size-xs);
  line-height: 16px;
}

.nowBadge {
  border-color: var(--accent);
  background: var(--accent);
  color: var(--white);
}

/* ---- titled blocks (2026-10b §5.2): the title answers "what was this", the project name is
   second-level; one text colour, hierarchy by size and weight. ---- */
.blockTitle {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: 600;
  font-size: 13px;
  line-height: 1.1;
}

.titledTallTitle {
  font-size: var(--font-size);
}

/* Single-line (18-32px): 12px title, 12px project name; the name gives way first. */
.titledSmall {
  font-size: var(--font-size-sm);
}

.titledName {
  flex: 0 100000 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: 500;
  font-size: var(--font-size-sm);
  line-height: 1.1;
}

/* Second line "project · range · duration": duration gives way first, then range, then name. */
.titledMeta {
  display: flex;
  min-width: 0;
  max-width: 480px;
  font-size: var(--font-size-sm);
  font-weight: 500;
  line-height: 1.1;
  font-variant-numeric: tabular-nums;
}

.titledMetaName,
.titledMetaRange,
.titledMetaDuration {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.titledMetaName {
  flex: 0 1 auto;
}

.titledMetaRange {
  flex: 0 100 auto;
}

.titledMetaDuration {
  flex: 0 10000 auto;
}

/* The time follows the name directly (§3.7), it is not pushed to the block's right edge. */
.meta {
  flex: none;
  padding-left: 6px;
  font-size: var(--font-size-xs);
  font-variant-numeric: tabular-nums;
}

/* First line of the block's note (ui-redesign §3.7): rendered only for day blocks at least
   44px tall that sit in no overlap column; shown once the lane is at least 440px wide. */
.note {
  display: none;
  max-width: 480px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: var(--font-size-sm);
  opacity: 0.85;
}

@container lane (min-width: 440px) {
  .note {
    display: block;
  }
}

.handleStart,
.handleEnd {
  position: absolute;
  left: 0;
  right: 0;
  z-index: 3;
  /* Same formula as the pointer hit test (§3.2.2 edge bands): max(4px, min(8px, 25%)). */
  height: clamp(4px, 25%, 8px);
  cursor: ns-resize;
  touch-action: none;
}

.handleStart {
  top: 0;
}

.handleEnd {
  bottom: 0;
}

/* Handle pills (ui-redesign §3.4.2): 28×6px (week 20×5px), centred across the block edge —
   half inside, half outside — white fill, 1.5px project-colour border. */
.handleStart::after,
.handleEnd::after {
  content: '';
  position: absolute;
  left: 50%;
  width: 28px;
  height: 6px;
  margin-left: -14px;
  border: 1.5px solid var(--block-color);
  border-radius: 3px;
  background: var(--surface);
  box-shadow: var(--shadow-sm);
  opacity: 0;
  transition: opacity 100ms ease-out;
}

.handleStart::after {
  top: -3px;
}

.handleEnd::after {
  bottom: -3px;
}

.weekLane .handleStart::after,
.weekLane .handleEnd::after {
  width: 20px;
  height: 5px;
  margin-left: -10px;
}

.weekLane .handleStart::after {
  top: -2.5px;
}

.weekLane .handleEnd::after {
  bottom: -2.5px;
}

.block:hover .handleStart::after,
.block:hover .handleEnd::after,
.block:focus-visible .handleStart::after,
.block:focus-visible .handleEnd::after,
.selected .handleStart::after,
.selected .handleEnd::after {
  opacity: 1;
}

/* Blocks too short for text (< 18px) show pills only once selected (§3.4.2); dragging an
   unselected short block moves it, and its resize goes through the 4px outer zones. */
.tiny:not(.selected):hover .handleStart::after,
.tiny:not(.selected):hover .handleEnd::after {
  opacity: 0;
}

/* An unselected short block is a whole-block move target everywhere (§3.2.2): its handle
   elements take no presses — resizing goes through the outer zones, or the pills once selected. */
.tiny:not(.selected) .handleStart,
.tiny:not(.selected) .handleEnd {
  pointer-events: none;
}

/* Edge ownership rule ① (§3.2.2): only the selected block's pill reaches outside the block
   edge; the invisible pills of unselected blocks take no presses out there. */
.block:not(.selected) .handleStart::after,
.block:not(.selected) .handleEnd::after {
  pointer-events: none;
}

/* Pill hit area of the selected block: the pill plus a 6px ring, above the hit zones of the
   neighbouring blocks (§3.2.2 药丸附加命中区). */
.selected .handleStart,
.selected .handleEnd {
  z-index: 4;
}

.selected .handleStart::before,
.selected .handleEnd::before {
  content: '';
  position: absolute;
  left: 50%;
  width: 40px;
  height: 18px;
  margin-left: -20px;
}

.selected .handleStart::before {
  top: -9px;
}

.selected .handleEnd::before {
  bottom: -9px;
}

.weekLane .selected .handleStart::before,
.weekLane .selected .handleEnd::before {
  width: 32px;
  height: 17px;
  margin-left: -16px;
}

.weekLane .selected .handleStart::before {
  top: -8.5px;
}

.weekLane .selected .handleEnd::before {
  bottom: -8.5px;
}

/* The handle fills solid project colour while the cursor is on the edge or dragging it. */
.handleStart:hover::after,
.handleEnd:hover::after,
[data-dragging] .selected .handleStart::after,
[data-dragging] .selected .handleEnd::after {
  background: var(--block-color);
}

/* Touch (§3.3): round 20px handles with 44×44px hit areas, visible only on the selected
   block — start handle top-left, end handle bottom-right. */
@media (pointer: coarse) {
  .handleStart,
  .handleEnd {
    left: auto;
    right: auto;
    width: 44px;
    height: 44px;
    pointer-events: none;
  }

  .selected .handleStart,
  .selected .handleEnd {
    pointer-events: auto;
  }

  .handleStart {
    top: -22px;
    left: 4px;
  }

  .handleEnd {
    bottom: -22px;
    right: 4px;
  }

  .handleStart::after,
  .handleEnd::after {
    top: 50%;
    left: 50%;
    width: 20px;
    height: 20px;
    margin: -10px 0 0 -10px;
    border-radius: 50%;
    opacity: 0;
  }

  .handleEnd::after {
    bottom: auto;
  }

  .block:hover .handleStart::after,
  .block:hover .handleEnd::after {
    opacity: 0;
  }

  .selected .handleStart::after,
  .selected .handleEnd::after {
    opacity: 1;
  }

  /* Touch handles already carry their own 44×44px hit areas: no pill expansion ring. */
  .selected .handleStart::before,
  .selected .handleEnd::before {
    content: none;
  }
}

/* The overlap marker (ui-redesign §3.8): a 16px two-rectangles icon at the top right corner,
   inside a 24x24px hit area (WCAG 2.2 minimum target size). */
.overlapBadge {
  position: absolute;
  top: 0;
  right: 0;
  z-index: 4;
  display: grid;
  place-items: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border: 0;
  background: none;
  cursor: pointer;
}

.overlapIcon {
  width: 16px;
  height: 16px;
  fill: var(--accent);
  stroke: var(--white);
  stroke-width: 1;
}

/* No room for the icon (block under 18px or column under 56px): a 6px accent strip on the
   block's right edge, still with a 24px wide hit area (§3.8). */
.overlapBar {
  top: 0;
  right: 0;
  bottom: 0;
  width: 24px;
  height: auto;
  place-items: stretch end;
}

.overlapBar .overlapIcon {
  display: none;
}

.overlapBar::after {
  content: '';
  width: 6px;
  background: var(--accent);
}

/* ---- week variant: narrow equal-width lanes ---- */
.weekLane .block {
  padding: 0 18px 0 5px;
}

.weekLane .compact {
  padding: 0 18px 0 5px;
}

.weekLane .meta,
.weekLane .nowBadge {
  display: none;
}

.weekLane .blockBody {
  gap: 4px;
}

.weekLane .badge {
  padding: 0 4px;
  line-height: 14px;
}

/* A narrow week lane cannot fit name and badge: the draft badge shrinks to a dot. */
@container (max-width: 130px) {
  .weekLane .badge {
    width: 8px;
    height: 8px;
    padding: 0;
    border-radius: 50%;
    background: currentcolor;
    font-size: 0;
  }
}

/* A week-lane block 30px or taller may wrap its name (or block title, §5.2) onto two lines. */
.weekLane .roomy .name {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  white-space: normal;
  line-height: 1.2;
}

.weekLane .gap {
  padding: 0 4px;
}

.openDay {
  position: absolute;
  top: 2px;
  right: 2px;
  z-index: 5;
  display: grid;
  place-items: center;
  width: 16px;
  height: 16px;
  border-radius: 4px;
  color: inherit;
  text-decoration: none;
  opacity: 0.55;
}

.openDay:hover,
.openDay:focus-visible {
  background: color-mix(in srgb, var(--block-color) 18%, transparent);
  opacity: 1;
}

.compact .openDay {
  opacity: 0;
}

.block:hover .openDay,
.selected .openDay,
.compact .openDay:focus-visible {
  opacity: 1;
}

.conflicting .openDay {
  display: none;
}

/* ---- ghosts, floating tip, now line ---- */
.ghost {
  position: absolute;
  left: 0;
  /* Previews take the width a block would take: the create band stays free. */
  right: var(--create-band);
  z-index: 4;
  display: flex;
  align-items: center;
  padding: 0 10px;
  border-radius: var(--radius-sm);
  pointer-events: none;
}

.ghost-drag {
  border: 1.5px dashed var(--accent);
  background: color-mix(in srgb, var(--accent) 12%, transparent);
}

.ghost-blocked {
  border: 1.5px dashed var(--danger);
  background: color-mix(in srgb, var(--danger) 8%, transparent);
}

.ghost-pending {
  border: 1.5px solid var(--accent);
  background: color-mix(in srgb, var(--accent) 10%, var(--white));
}

.ghost-hover {
  border: 1px dashed color-mix(in srgb, var(--accent) 60%, transparent);
  background: color-mix(in srgb, var(--accent) 8%, transparent);
  opacity: 0.9;
}

.ghostLabel {
  color: var(--accent);
  font-size: var(--font-size-sm);
}

/* The floating tip of a drag: L4, a fixed element on document.body, never inside a lane
   (ui-redesign §3.4.3). It cannot change any lane's layout, scrollbars or grid. */
.floatTip {
  position: fixed;
  z-index: 60;
  padding: 4px 8px;
  border-radius: 6px;
  background: var(--text);
  color: var(--white);
  font-size: var(--font-size-sm);
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  box-shadow: var(--shadow-pop);
  pointer-events: none;
}

/* Guide lines at the start and end of a drag preview (ui-redesign §3.4.4). */
.guideLine {
  position: absolute;
  left: 0;
  right: 0;
  z-index: 1;
  height: 0;
  border-top: 1px dashed color-mix(in srgb, var(--accent) 60%, transparent);
  pointer-events: none;
}

/* Under the time blocks (z-index 2), above the hour lines (lane background) and the free-stretch
   slots (no z-index): visible in free time, hidden where a block covers it. */
.nowLine {
  position: absolute;
  left: -4px;
  right: 0;
  z-index: 1;
  height: 2px;
  margin-top: -1px;
  background: var(--now);
  box-shadow: 0 0 0 1px var(--white);
  pointer-events: none;
}

/* Right-end label of the day view's line, styled like the axis label (.axisNow); it is a child
   of the line, so it sits in the same layer and hides under a block with it. */
.nowLabel {
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  padding: 1px 5px;
  border-radius: 8px;
  background: var(--now);
  box-shadow: 0 0 0 1px var(--white);
  color: var(--white);
  font-size: var(--font-size-xs);
  line-height: 1.3;
  white-space: nowrap;
}

.nowLine::before {
  content: '';
  position: absolute;
  left: 0;
  top: -3px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--now);
  box-shadow: 0 0 0 1px var(--white);
}

/* ---- loading and error states ---- */
.skeleton {
  position: absolute;
  inset: 0;
  z-index: 7;
  background: color-mix(in srgb, var(--bg) 70%, transparent);
}

.skeletonBar {
  position: absolute;
  left: 0;
  right: 0;
  border-radius: var(--radius-sm);
  background: var(--surface-muted);
  animation: pulse 1.4s ease-in-out infinite;
}

.error {
  position: absolute;
  inset: 0;
  z-index: 7;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  background: color-mix(in srgb, var(--bg) 88%, transparent);
  color: var(--danger);
}

.retry {
  height: 30px;
  padding: 0 14px;
  border: 1px solid var(--border);
  border-radius: var(--radius-sm);
  background: var(--surface);
  color: var(--text);
}

@keyframes pulse {
  50% {
    opacity: 0.45;
  }
}

@media (prefers-reduced-motion: reduce) {
  .skeletonBar {
    animation: none;
  }
}

@media (max-width: 767px) {
  .timeline {
    grid-template-columns: 44px minmax(0, 1fr);
    column-gap: 6px;
  }
}

/* ---- ui-redesign WP4: drag follow, armed touch states, quick-mark preview, ghost text ---- */

/* Whole moves follow the pointer with transform: translate and no transition (§2.4): the
   geometry (top) stays at the original span, so releasing commits exactly what is shown.
   The x offset is the week view's cross-day move onto the target lane (§2.8), 0 otherwise. */
.shifted {
  transform: translate(var(--drag-shift-x, 0px), var(--drag-shift, 0px));
  transition: none;
  will-change: transform;
}

.dragging {
  transition: none;
  opacity: 0.96;
}

/* Touch long press armed a move (§3.3): lifted, slightly widened, accent ring. */
.armed {
  box-shadow:
    0 0 0 2px var(--accent),
    var(--shadow-lift);
  transform: scaleX(1.02);
}

.armed.shifted {
  transform: translate(var(--drag-shift-x, 0px), var(--drag-shift, 0px)) scaleX(1.02);
}

/* A draft dragged towards another day column stays on its own day (§2.8): the lane and the
   dragged card both show not-allowed while the pointer is past another lane's centre line. */
[data-cross-denied],
[data-cross-denied] .dragging {
  cursor: not-allowed;
}

/* Quick-mark mode: hovering a block previews the chosen project's colour (§3.2 QuickMark). */
.quickMark .block {
  cursor: pointer;
}

.quickTarget {
  cursor: pointer;
}

/* Armed create preview (§3.3): the dashed outline thickens to a solid accent border. */
.ghostArmed {
  border: 2px solid var(--accent);
  animation: ghost-arm 160ms ease-out;
}

@keyframes ghost-arm {
  from {
    transform: scale(0.98);
  }

  to {
    transform: scale(1);
  }
}

/* Dashed outline at the original place of a moved block (§3.4.4): no fill, project colour. */
.ghost-origin {
  border: 1.5px dashed var(--origin-color, color-mix(in srgb, var(--accent) 60%, transparent));
  background: transparent;
  z-index: 2;
}

/* Hover-ghost text (§3.4.1): the time range, plus the learning hint until it retires. */
.ghostText {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  pointer-events: none;
}

.ghostRange {
  color: var(--accent);
  font-size: var(--font-size-sm);
  font-variant-numeric: tabular-nums;
}

.ghostHint {
  color: var(--accent);
  font-size: var(--font-size-sm);
  opacity: 0.85;
}

/* Second line of the floating tip (§3.4.3). */
.floatTipLine2 {
  opacity: 0.85;
}

.floatTipWarn {
  color: color-mix(in srgb, var(--danger) 45%, var(--white));
  font-weight: 600;
}

@media (prefers-reduced-motion: reduce) {
  .ghostArmed {
    animation: none;
  }
}
